import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StacSessionService } from './stac-session.service';

/**
 * Per-user scene annotations — tags, ref ID, owner, classification and whatever else is
 * configured — the Angular side of plans/stac-custom-metadata.md §7.1 (D13, R19, R27), built on
 * plans/stac-custom-tags.md §5 and §6 Trap 5.
 *
 * HttpClient reads use the HttpOnly session cookie; writes use the separate tags:write token.
 * StacSessionService owns credentials and renewal. STAC calls bypass the platform interceptors'
 * header replacement and blanket logout behavior. This service retains only per-account data caches.
 *
 * THE FIELDS COME FROM THE SERVER. `GET /annotation-fields` lists them — titles, hints, kinds and
 * the limits a save enforces — once per session. Nothing here names a field, so a field a
 * migration adds appears after an API restart and a page reload with no Angular release; a new
 * KIND needs one. The server stays the authority: the checks below only hint before a round trip.
 *
 * THIS SERVICE OWNS THE PER-TASK CACHE. One GET of a task's items answers "which of this task's
 * scenes are in the catalog, and what are MY annotations on them" for every scene row of that
 * task; the per-scene row component asks for it and the in-flight promise is shared, so N rows
 * cost one request. Only ~11.6 % of platform scenes are catalog items (job §1b) — a scene absent
 * from the map simply gets no editor.
 *
 * A SAVE SENDS ONLY WHAT CHANGED (R19). The row component works out which fields differ from the
 * row's saved snapshot and passes exactly those; sending every displayed field would let a stale
 * tab resubmit its old copy of a field another tab had just changed.
 */

/** One configured field, as `GET /annotation-fields` describes it (plans/stac-custom-metadata.md §3.6). */
export interface AnnotationField {
  /** The Item property, e.g. `ee:owner`. */
  key: string;
  /** The save body's member, e.g. `owner`. */
  member: string;
  kind: 'tagset' | 'text' | 'enum';
  title: string;
  description?: string;
  /** tagset: at most this many entries. */
  maxItems?: number;
  /** tagset: each entry; text: the value. */
  maxLength?: number;
  /** tagset: each entry, after trimming. */
  pattern?: string;
  /** enum: the allowed values, in rank order. */
  values?: string[];
  /** enum: value → label. */
  labels?: Record<string, string>;
}

/**
 * The caller's annotation on one scene, by member: a tag list (`[]` when none) for a tagset, a
 * value or `null` otherwise. As a SAVE it holds only the fields being changed, with `null` (or
 * `[]` for tags) for a field being cleared (§4.1).
 */
export type Annotation = Record<string, string[] | string | null>;

export type StacTagsErrorKind =
  | 'forbidden' // the credential may read but not write (API keys, query-string tokens)
  | 'not_found' // the scene is not in the catalog for this user
  | 'invalid' // the backend refused a value
  | 'unauthorized' // no usable embed token even after one re-mint
  | 'network'
  | 'unknown';

export class StacTagsError extends Error {
  constructor(public readonly kind: StacTagsErrorKind, message: string, public readonly status?: number) {
    super(message);
    this.name = 'StacTagsError';
  }
}

/** A page is at most the route's MAX_LIMIT (params.js); 50 pages covers the 5 000-row per-user cap. */
const PAGE_LIMIT = 100;
const MAX_PAGES = 50;
const KINDS = ['tagset', 'text', 'enum'];

@Injectable({ providedIn: 'root' })
export class StacTagsService {
  /** Platform JWT string the caches below belong to. */
  private cacheOwner: string | null = null;
  private byTask = new Map<string, Map<string, Annotation>>();
  private inFlight = new Map<string, Promise<Map<string, Annotation>>>();
  private definitions: AnnotationField[] | null = null;
  /** Every member the definitions response named — the editable fields and any this client cannot show. */
  private definitionMembers: string[] | null = null;
  private definitionsInFlight: Promise<AnnotationField[]> | null = null;

  constructor(private http: HttpClient, private session: StacSessionService) {}

  /**
   * `{taskid}__{sensor_uuid}__{scene_id || id}` — job §0's item id rule, the ONLY way to relate a
   * platform scene row to a catalog item. The collections page rebuilds its objects
   * (leaflet-newcollections.component.ts, `this.collections.push({...})`): the task id is
   * `collection.taskId` and the sensor uuid is `sensor.id` on those rebuilt objects. Returns null
   * when any part is missing rather than inventing an id that could never resolve.
   */
  static candidateItemId(taskId: unknown, sensorUuid: unknown, scene: any): string | null {
    const sceneId = scene && (scene.scene_id || scene.id);
    if (!isNonEmpty(taskId) || !isNonEmpty(sensorUuid) || !isNonEmpty(sceneId)) return null;
    return `${taskId}__${sensorUuid}__${sceneId}`;
  }

  /**
   * A hint for a tag about to be added, or null when it would pass the backend's canonicaliser —
   * from the field's own definition (pattern, length, count), never from a copy kept here.
   */
  static tagProblem(field: AnnotationField, raw: string, existing: string[]): string | null {
    const tag = (raw || '').trim();
    if (tag === '') return null;
    if (typeof field.maxLength === 'number' && tag.length > field.maxLength) {
      return `${field.title}: at most ${field.maxLength} characters each.`;
    }
    if (field.pattern && !safeRegExp(field.pattern).test(tag)) {
      return 'Use lowercase letters, digits, dots, dashes or underscores — no spaces.';
    }
    if (existing.indexOf(tag) !== -1) return 'That tag is already on this scene.';
    if (typeof field.maxItems === 'number' && existing.length >= field.maxItems) {
      return `At most ${field.maxItems} per scene.`;
    }
    return null;
  }

  /** A hint for a text or enum value, or null. Empty is fine: the row sends it as a clear. */
  static valueProblem(field: AnnotationField, value: string | null): string | null {
    if (value === null || value.trim() === '') return null;
    if (field.kind === 'text') {
      const text = value.trim();
      if (typeof field.maxLength === 'number' && text.length > field.maxLength) {
        return `${field.title}: at most ${field.maxLength} characters.`;
      }
      if (/[\r\n]/.test(text)) return `${field.title}: one line only.`;
    }
    if (field.kind === 'enum' && !(field.values || []).includes(value)) return `${field.title}: choose one of the listed values.`;
    return null;
  }

  /**
   * The configured fields, in display order — fetched once per session, concurrent callers
   * sharing one request. Definitions change only with an API restart, so a tab keeps its copy
   * until a save shows it is stale (see `setAnnotation`).
   */
  fieldDefinitions(): Promise<AnnotationField[]> {
    this.assertOwner();
    if (this.definitions) return Promise.resolve(this.definitions);
    if (this.definitionsInFlight) return this.definitionsInFlight;
    const p = this.call('GET', `${environment.stacApiUrl}/annotation-fields`)
      .then((body) => {
        const fields = parseFields(body);
        this.definitions = fields;
        this.definitionMembers = rawMembers(body);
        return fields;
      })
      .finally(() => {
        if (this.definitionsInFlight === p) this.definitionsInFlight = null;
      });
    this.definitionsInFlight = p;
    return p;
  }

  /** Synchronous read of the cache: undefined = not loaded or not a catalog item. */
  cachedAnnotation(taskId: string, itemId: string): Annotation | undefined {
    this.assertOwner();
    const task = this.byTask.get(taskId);
    return task ? task.get(itemId) : undefined;
  }

  /**
   * Every catalog item of a task, mapped to the caller's annotation on it — every defined field,
   * empty ones as `[]` or `null`. Read through the task's item listing, where the backend merges
   * each field into `properties` (D10). Cached per task; concurrent callers share one request.
   */
  annotationsForTask(taskId: string): Promise<Map<string, Annotation>> {
    this.assertOwner();
    const cached = this.byTask.get(taskId);
    if (cached) return Promise.resolve(cached);
    const pending = this.inFlight.get(taskId);
    if (pending) return pending;
    const p = this.fieldDefinitions()
      .then((fields) => this.fetchTaskItems(taskId, fields))
      .then((map) => {
        this.byTask.set(taskId, map);
        return map;
      })
      .finally(() => this.inFlight.delete(taskId));
    this.inFlight.set(taskId, p);
    return p;
  }

  /**
   * Save the CHANGED fields of one scene's annotation — `changes` names only those (R19), with
   * `null` or `[]` for a field changed to empty. The backend answers the whole annotation as
   * STORED, every active field included; that, not what was sent, is what the cache keeps and
   * what is returned. A member the cached definitions lack means the configuration changed since
   * they were fetched (R27): they are dropped, so the next `fieldDefinitions()` fetches them again.
   */
  async setAnnotation(taskId: string, itemId: string, changes: Annotation): Promise<Annotation> {
    this.assertOwner();
    const url =
      `${environment.stacApiUrl}/collections/${encodeURIComponent(taskId)}` +
      `/items/${encodeURIComponent(itemId)}/annotations`;
    const stored = storedAnnotation(await this.call('PUT', url, changes));
    // Compared with every member the definitions named — not only the ones this client can edit —
    // so a field of a kind it does not know does not make every save fetch them again.
    const known = this.definitionMembers;
    if (known && Object.keys(stored).some((m) => !known.includes(m))) {
      this.definitions = null;
      this.definitionMembers = null;
    }
    const task = this.byTask.get(taskId);
    if (task) task.set(itemId, stored);
    return stored;
  }

  /** Drop every cache. Called automatically when the platform identity changes. */
  invalidate(): void {
    this.cacheOwner = null;
    this.byTask.clear();
    this.inFlight.clear();
    this.definitions = null;
    this.definitionMembers = null;
    this.definitionsInFlight = null;
  }

  // ─────────────────────────────────────────────────────────── the catalog reads

  private async fetchTaskItems(taskId: string, fields: AnnotationField[]): Promise<Map<string, Annotation>> {
    const out = new Map<string, Annotation>();
    let url: string | null =
      `${environment.stacApiUrl}/collections/${encodeURIComponent(taskId)}/items?limit=${PAGE_LIMIT}`;
    for (let page = 0; url && page < MAX_PAGES; page += 1) {
      const body = await this.call('GET', url);
      const features = body && Array.isArray(body.features) ? body.features : [];
      for (const f of features) {
        if (!f || !isNonEmpty(f.id)) continue;
        out.set(String(f.id), annotationFromProperties(f.properties, fields));
      }
      const next = body && Array.isArray(body.links) ? body.links.find((l: any) => l && l.rel === 'next' && isNonEmpty(l.href)) : null;
      url = next ? String(next.href) : null;
    }
    return out;
  }

  // ─────────────────────────────────────────────────────────── the side door

  /**
   * One request, answering the parsed JSON body. A 401 is retried exactly once after a forced
   * re-mint — that is how a token that expired while the page sat open recovers without a logout.
   * Anything else non-2xx becomes a typed StacTagsError the UI can word without reading the body.
   */
  private async call(method: 'GET' | 'PUT', url: string, body?: unknown, retried = false): Promise<any> {
    const platform = this.session.platformToken();
    const target = new URL(url, window.location.origin);
    const base = new URL(environment.stacApiUrl, window.location.origin);
    if (target.origin !== window.location.origin || target.origin !== base.origin ||
        !target.pathname.startsWith(base.pathname + '/')) {
      throw new StacTagsError('forbidden', 'The catalog returned an external URL.');
    }
    let token: string;
    try { token = await this.session.ensure(retried); }
    catch { throw new StacTagsError('unauthorized', 'The catalog session could not be renewed.'); }
    let status: number;
    let payload: any;
    try {
      const response = await firstValueFrom(this.http.request(method, url, {
        body, observe: 'response',
        headers: method === 'PUT' ? { Authorization: `Bearer ${token}` } : {},
      }));
      status = response.status;
      payload = response.body;
    } catch (e) {
      if (!(e instanceof HttpErrorResponse) || !e.status) throw new StacTagsError('network', 'The catalog could not be reached.');
      status = e.status;
      payload = e.error;
    }
    if (platform !== this.session.platformToken()) throw new StacTagsError('unauthorized', 'The account changed.');
    if (status >= 200 && status < 300) return payload;
    if (status === 401 && !retried) return this.call(method, url, body, true);
    switch (status) {
      case 401:
        throw new StacTagsError('unauthorized', 'The catalog session could not be renewed.', 401);
      case 403:
        throw new StacTagsError('forbidden', 'This session may read the catalog but not change it.', 403);
      case 404:
        throw new StacTagsError('not_found', 'This scene is not in the catalog.', 404);
      case 400: {
        // The backend's descriptions are fixed server text (never an echo of the input), so they
        // are safe to show.
        const description = payload && typeof payload.description === 'string' ? payload.description : 'The changes were not accepted.';
        throw new StacTagsError('invalid', description, 400);
      }
      default:
        throw new StacTagsError('unknown', 'The catalog could not save the changes.', status);
    }
  }

  /** If the platform identity changed since the caches were filled, drop them. */
  private assertOwner(): void {
    const platform = this.session.platformToken();
    if (this.cacheOwner !== platform) {
      this.invalidate();
      this.cacheOwner = platform;
    }
  }
}

/** The definitions response, keeping only well-formed entries of a known kind. */
function parseFields(body: any): AnnotationField[] {
  const list = body && Array.isArray(body.fields) ? body.fields : [];
  return list.filter((f: any) =>
    f && isNonEmpty(f.key) && isNonEmpty(f.member) && isNonEmpty(f.title) && KINDS.includes(f.kind) &&
    (f.kind !== 'enum' || (Array.isArray(f.values) && f.values.every(isNonEmpty)))
  );
}

/** Every `member` the definitions response names, well-formed entry or not. */
function rawMembers(body: any): string[] {
  const list = body && Array.isArray(body.fields) ? body.fields : [];
  return list.filter((f: any) => f && isNonEmpty(f.member)).map((f: any) => f.member as string);
}

/** One Item's `properties`, read field by field: absent is `[]` for a tagset and `null` otherwise. */
function annotationFromProperties(properties: any, fields: AnnotationField[]): Annotation {
  const out: Annotation = {};
  for (const field of fields) {
    const raw = properties ? properties[field.key] : undefined;
    if (field.kind === 'tagset') out[field.member] = Array.isArray(raw) ? raw.filter(isNonEmpty).map(String) : [];
    else out[field.member] = isNonEmpty(raw) ? raw : null;
  }
  return out;
}

/** The save's `200` body: every member it carries, a list as strings and anything else as a value or null. */
function storedAnnotation(body: any): Annotation {
  const out: Annotation = {};
  if (!body || typeof body !== 'object' || Array.isArray(body)) return out;
  for (const member of Object.keys(body)) {
    const value = body[member];
    if (Array.isArray(value)) out[member] = value.filter(isNonEmpty).map(String);
    else out[member] = isNonEmpty(value) ? value : null;
  }
  return out;
}

/** A definition's pattern; one the browser cannot compile checks nothing rather than everything. */
function safeRegExp(pattern: string): RegExp {
  try {
    return new RegExp(pattern);
  } catch {
    return /[\s\S]*/;
  }
}

function isNonEmpty(v: unknown): v is string {
  return typeof v === 'string' && v !== '';
}
